import 'src/global.css';
import '@blocknote/mantine/style.css';

import { useEffect } from 'react';

import { usePathname } from 'src/routes/hooks';

import { themeConfig, ThemeProvider } from 'src/theme';
import { I18nProvider } from 'src/locales/i18n-provider';
import { LocalizationProvider, useLocaleDirectionSync } from 'src/locales';

import { Snackbar } from 'src/components/snackbar';
import { Favicon } from 'src/components/favicon/favicon';
import { ProgressBar } from 'src/components/progress-bar';
import { MotionLazy } from 'src/components/animate/motion-lazy';
import { SettingsDrawer, defaultSettings, SettingsProvider } from 'src/components/settings';

import { AuthProvider as JwtAuthProvider } from 'src/auth/context/jwt';

import QueryProvider from './lib/QueryProvider';

// ----------------------------------------------------------------------

type AppProps = {
  children: React.ReactNode;
};

export default function App({ children }: AppProps) {
  useScrollToTop();

  return (
    <I18nProvider>
      <QueryProvider>
        <JwtAuthProvider>
          <SettingsProvider defaultSettings={defaultSettings}>
            <LocaleDocumentSync />
            <LocalizationProvider>
              <ThemeProvider
                modeStorageKey={themeConfig.modeStorageKey}
                defaultMode={themeConfig.defaultMode}
              >
                <MotionLazy>
                  <Favicon />
                  <Snackbar />
                  <ProgressBar />
                  <SettingsDrawer defaultSettings={defaultSettings} />
                  {children}
                </MotionLazy>
              </ThemeProvider>
            </LocalizationProvider>
          </SettingsProvider>
        </JwtAuthProvider>
      </QueryProvider>
    </I18nProvider>
  );
}

// ----------------------------------------------------------------------

function LocaleDocumentSync() {
  useLocaleDirectionSync();

  return null;
}

// ----------------------------------------------------------------------

function useScrollToTop() {
  const pathname = usePathname();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}
